import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import {
  LocationOn,
  People,
  Euro,
  AccessTime,
  Person,
  Schedule,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import {
  getCategoryLabel,
  getCategoryColor,
  getDayName,
  formatDuration,
} from '../../utils/reservableHelpers';

const ReservablePreviewCard = ({ reservable, title = 'Anteprima member' }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      {title}
    </Typography>
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            label={getCategoryLabel(reservable.category)}
            color={getCategoryColor(reservable.category)}
            size="small"
          />
          {reservable.requiresApproval && (
            <Chip
              label="Richiede approvazione"
              color="warning"
              size="small"
              icon={<Schedule />}
            />
          )}
          {(reservable.maxConcurrentAccess ?? 1) > 1 && (
            <Chip
              label={`Fino a ${reservable.maxConcurrentAccess} accessi`}
              color="info"
              size="small"
            />
          )}
          {!reservable.isActive && (
            <Chip label="Non attiva" color="default" size="small" />
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {reservable.name}
        </Typography>

        {reservable.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {reservable.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LocationOn sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {reservable.location || 'Non specificata'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <People sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Capacità: {reservable.capacity} {reservable.capacity === 1 ? 'persona' : 'persone'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Euro sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {reservable.costPerHour}€/ora
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Durate:{' '}
            {(reservable.availableDurations || [])
              .map((d) => formatDuration(d))
              .join(', ')}
          </Typography>
        </Box>

        {reservable.teacher && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Person sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Istruttore: {reservable.teacher.name} {reservable.teacher.surname}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Disponibilità:
        </Typography>
        {(reservable.availability || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nessun giorno configurato
          </Typography>
        ) : (
          reservable.availability.map((avail, index) => (
            <Chip
              key={index}
              label={`${getDayName(avail.dayOfWeek)} ${avail.startTime}-${avail.endTime}`}
              size="small"
              sx={{ mr: 1, mb: 1 }}
            />
          ))
        )}
      </CardContent>

      <CardActions>
        <Button
          variant="contained"
          fullWidth
          disabled={!reservable.isActive}
          startIcon={reservable.isActive ? <CheckCircle /> : <Cancel />}
        >
          {reservable.isActive ? 'Prenota' : 'Non disponibile'}
        </Button>
      </CardActions>
    </Card>
  </Box>
);

export default ReservablePreviewCard;
